import { defaultMaterialProperties, type MaterialProperties } from '../hooks/useMaterialProperties';
import type { AdminPreviewCalibrationDraft } from '../hooks/useAdminPreviewState';
import type { PublicRenderPipeline, PublicRenderPipelineOverrides } from './publicRenderPipelines';

export const toPublicRenderPipelineOverrides = (
  pipeline: PublicRenderPipeline
): PublicRenderPipelineOverrides => ({
  showEnvironmentBackground: pipeline.showEnvironmentBackground,
  environmentBackgroundBlur: pipeline.environmentBackgroundBlur,
  showContactShadow: pipeline.showContactShadow,
  shadowOpacity: pipeline.shadowOpacity,
  shadowRadiusMultiplier: pipeline.shadowRadiusMultiplier,
  shadowPlaneEpsilon: pipeline.shadowPlaneEpsilon,
  reflectionEnabled: pipeline.reflectionEnabled,
  reflectionOpacity: pipeline.reflectionOpacity,
  reflectionVisibility: pipeline.reflectionVisibility,
  floorPlaneMode: pipeline.floorPlaneMode,
  reflectionPlaneYOffset: pipeline.reflectionPlaneYOffset,
  reflectorSurfaceOpacity: pipeline.reflectorSurfaceOpacity,
  reflectionRadiusMultiplier: pipeline.reflectionRadiusMultiplier,
  reflectionForwardOffsetMultiplier: pipeline.reflectionForwardOffsetMultiplier,
  reflectionWidthMultiplier: pipeline.reflectionWidthMultiplier,
  reflectionDepthMultiplier: pipeline.reflectionDepthMultiplier,
  reflectionForegroundFade: pipeline.reflectionForegroundFade,
  reflectionDepthFade: pipeline.reflectionDepthFade,
  reflectionNearBaseSharpness: pipeline.reflectionNearBaseSharpness,
  reflectionViewerSideSoftness: pipeline.reflectionViewerSideSoftness,
  reflectionResolution: pipeline.reflectionResolution,
  floorSurfaceOpacity: pipeline.floorSurfaceOpacity,
  floorColor: pipeline.floorColor,
  cameraTargetMode: pipeline.cameraTargetMode,
  fixedCameraTarget: pipeline.fixedCameraTarget,
  modelPosition: pipeline.modelPosition,
  cameraTargetOffset: pipeline.cameraTargetOffset,
  cameraViewAzimuth: pipeline.cameraViewAzimuth,
  cameraViewElevation: pipeline.cameraViewElevation,
  cameraViewDistance: pipeline.cameraViewDistance,
  environmentRotation: pipeline.environmentRotation,
  modelScale: pipeline.modelScale,
  autoRotateModel: pipeline.autoRotateModel,
  dragYawSensitivity: pipeline.dragYawSensitivity,
  autoRotateSpeed: pipeline.autoRotateSpeed,
  autoRotateDirection: pipeline.autoRotateDirection,
  tiltMaxDeg: pipeline.tiltMaxDeg,
  tiltSensitivity: pipeline.tiltSensitivity,
  tiltResetOnRelease: pipeline.tiltResetOnRelease,
});

export const materialFallbackFromProperties = (
  properties?: Partial<MaterialProperties> | null
): MaterialProperties => ({
  ...defaultMaterialProperties,
  ...(properties ?? {}),
});

type CalibrationSettings = AdminPreviewCalibrationDraft['settings'];

export type ColourCalibrationRecord = {
  colour_name?: string;
  contrast?: number | null;
  brightness_multiplier?: number | null;
  gamma_correction?: number | null;
  saturation?: number | null;
};

export type CalibratedViewerColour = {
  name: string;
  rgb: { r: number; g: number; b: number };
  saturation: number;
};

const DEFAULT_CALIBRATION_SETTINGS: Required<CalibrationSettings> = {
  contrast: 1.0,
  brightness_multiplier: 1.5,
  gamma_correction: 2.2,
  saturation: 1.0,
};

const isValidHexColor = (value: string) => /^#[0-9A-Fa-f]{6}$/.test(value);

const normalizeHex = (value: string) => {
  const trimmed = value.trim();
  return trimmed.startsWith('#') ? trimmed.toUpperCase() : `#${trimmed.toUpperCase()}`;
};

const hexToLinearRgb = (hex: string): { r: number; g: number; b: number } => {
  const cleanHex = hex.replace('#', '');
  const r255 = parseInt(cleanHex.substring(0, 2), 16);
  const g255 = parseInt(cleanHex.substring(2, 4), 16);
  const b255 = parseInt(cleanHex.substring(4, 6), 16);
  let r = r255 / 255;
  let g = g255 / 255;
  let b = b255 / 255;
  r = r <= 0.04045 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  g = g <= 0.04045 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  b = b <= 0.04045 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  return { r, g, b };
};

const applyColourCalibration = (
  rgb: { r: number; g: number; b: number },
  settings: Required<CalibrationSettings>
) => {
  let newR = rgb.r * settings.brightness_multiplier;
  let newG = rgb.g * settings.brightness_multiplier;
  let newB = rgb.b * settings.brightness_multiplier;

  if (settings.saturation !== 1.0) {
    const max = Math.max(newR, newG, newB);
    const min = Math.min(newR, newG, newB);
    const lightness = (max + min) / 2;
    if (max !== min) {
      newR = lightness + (newR - lightness) * settings.saturation;
      newG = lightness + (newG - lightness) * settings.saturation;
      newB = lightness + (newB - lightness) * settings.saturation;
    }
  }

  newR = Math.pow(Math.max(0, newR), 1.0 / settings.gamma_correction);
  newG = Math.pow(Math.max(0, newG), 1.0 / settings.gamma_correction);
  newB = Math.pow(Math.max(0, newB), 1.0 / settings.gamma_correction);

  const center = 0.5;
  newR = center + (newR - center) * settings.contrast;
  newG = center + (newG - center) * settings.contrast;
  newB = center + (newB - center) * settings.contrast;

  return {
    r: Math.max(0, Math.min(1, newR)),
    g: Math.max(0, Math.min(1, newG)),
    b: Math.max(0, Math.min(1, newB)),
  };
};

const settingsFromSavedCalibration = (
  saved?: ColourCalibrationRecord
): Required<CalibrationSettings> | undefined => {
  if (!saved) return undefined;
  return {
    contrast: saved.contrast || DEFAULT_CALIBRATION_SETTINGS.contrast,
    brightness_multiplier:
      saved.brightness_multiplier ?? DEFAULT_CALIBRATION_SETTINGS.brightness_multiplier,
    gamma_correction: saved.gamma_correction || DEFAULT_CALIBRATION_SETTINGS.gamma_correction,
    saturation: saved.saturation ?? DEFAULT_CALIBRATION_SETTINGS.saturation,
  };
};

export const buildCalibratedViewerColours = (
  colours: string[] | undefined,
  calibrationDraftsByColourKey: Record<string, AdminPreviewCalibrationDraft> = {},
  savedCalibrations: Record<string, ColourCalibrationRecord> = {}
): CalibratedViewerColour[] | undefined => {
  if (!colours?.length) return undefined;
  const normalizedColours = colours.map(normalizeHex);
  if (!normalizedColours.every(isValidHexColor)) return undefined;

  return normalizedColours.map((hex) => {
    const settings =
      calibrationDraftsByColourKey[hex]?.settings ??
      settingsFromSavedCalibration(savedCalibrations[hex]) ??
      DEFAULT_CALIBRATION_SETTINGS;
    const rgb = hexToLinearRgb(hex);
    return {
      name: hex,
      rgb: applyColourCalibration(rgb, {
        ...DEFAULT_CALIBRATION_SETTINGS,
        ...settings,
      }),
      saturation: settings.saturation ?? 1.0,
    };
  });
};
